import { useEffect, useRef, useState } from "react";
import { submitLead } from "@/lib/submit-lead.functions";
import { getTurnstileToken, preloadTurnstile } from "@/lib/turnstile";
import { captureTrackingParams, type TrackingParams } from "@/lib/tracking-params";
import { hashUserData, splitName } from "@/lib/hash-user-data";
import { pushLeadEvent } from "@/lib/lead-event";
import { SeasonNotice } from "./season-banner";


const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const SERVICE_OPTIONS = [
  { value: "house", label: "House Washing" },
  { value: "roof", label: "Roof Washing" },
  { value: "homecare", label: "Home Care Membership" },
  { value: "concrete", label: "Concrete & Driveway" },
  { value: "pavers", label: "Paver Cleaning" },
  { value: "deck", label: "Deck & Patio" },
  { value: "gutter", label: "Gutter Cleaning" },
  { value: "window", label: "Window Cleaning" },
  { value: "fence", label: "Fence Cleaning" },
  { value: "rejuvenation", label: "Roof Rejuvenation" },
  { value: "unsure", label: "Not sure / Multiple" },
];

/**
 * Maps a page/service slug to the matching option above so service, city and
 * landing pages can pre-select what the visitor is already reading about.
 */
export function serviceValueForSlug(slug?: string): string {
  if (!slug) return "";
  const s = slug.toLowerCase();
  const rules: [RegExp, string][] = [
    [/roof/, "roof"],
    [/gutter/, "gutter"],
    [/window/, "window"],
    [/fence/, "fence"],
    [/deck|patio/, "deck"],
    [/paver/, "pavers"],
    [/concrete|driveway|sidewalk|hardscape|sealing/, "concrete"],
    [/membership|annual|maintenance|homecare/, "homecare"],
    [/house|siding|exterior|soft-wash|softwash/, "house"],
  ];
  for (const [rx, value] of rules) if (rx.test(s)) return value;
  return "";
}

function digitsOnly(v: string) {
  return v.replace(/\D/g, "");
}
function formatPhone(v: string) {
  const d = digitsOnly(v).slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
function isValidUsPhone(v: string) {
  const d = digitsOnly(v);
  return d.length === 10 && !/^[01]/.test(d);
}

/**
 * Compact lead capture (service + name + phone + email) for visitors who want the
 * fastest possible path. Submits through the same server function as the wizard
 * and lands on /thank-you.
 */
export function QuickLeadForm({
  className = "",
  source = "Quick Quote",
  showHeader = true,
  defaultService = "",
}: {
  className?: string;
  source?: string;
  showHeader?: boolean;
  /** Pre-selected service option value (see `serviceValueForSlug`). */
  defaultService?: string;
}) {
  const [services, setServices] = useState<string[]>(defaultService ? [defaultService] : []);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tracking, setTracking] = useState<TrackingParams | null>(null);
  const sending = useRef(false);

  useEffect(() => {
    setTracking(captureTrackingParams());
    // Warm the bot-check script now so submit stays instant.
    preloadTurnstile();
  }, []);

  function toggleService(value: string) {
    setServices((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
    setError("");
  }

  const serviceLabels = services.map(
    (v) => SERVICE_OPTIONS.find((o) => o.value === v)?.label ?? v,
  );

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const full = name.trim();
    const cleanEmail = email.trim();
    if (services.length === 0) return setError("Please select at least one service.");
    if (full.length < 2) return setError("Please enter your full name.");
    if (!isValidUsPhone(phone)) return setError("Enter a valid 10-digit phone number.");
    if (cleanEmail.length > 0 && !emailRx.test(cleanEmail)) return setError("Enter a valid email address.");
    setError("");
    if (sending.current) return;
    sending.current = true;
    setBusy(true);

    const nameParts = full.split(/\s+/);
    const cleanPhone = digitsOnly(phone);
    const eventId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `lead_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    void (async () => {
      // Save in the background — the conversion event must not wait on it.
      // The bot check runs inside this chain so it never delays analytics.
      const savePromise = getTurnstileToken().then((turnstileToken) =>
        submitLead({
        data: {
          turnstileToken,
          fullName: full,
          phone: cleanPhone,
          email: cleanEmail,
          address: "",
          city: "",
          state: "NY",
          zip: "",
          outOfArea: false,
          propertyType: "Home",
          services,
          timeline: "",
          message: `Quick quote request from ${source}. Services: ${serviceLabels.join(", ")}`,
          bundleDiscount: services.length >= 2,
          serviceCount: services.length,
          plan: "",
          tracking: tracking ?? captureTrackingParams(),
          eventId,
        },
        }),
      ).catch((err) => {
        console.error("[quick-lead] submit error", err);
      });
      // Meta lead tracking is delegated to GTM via the `lead_submitted` dataLayer push below.
      let analyticsSent: Promise<void> = Promise.resolve();
      try {
        const { firstName, lastName } = splitName(full);
        let hashed: Awaited<ReturnType<typeof hashUserData>> | null = null;
        try {
          hashed = await hashUserData({
            fullName: full,
            email: cleanEmail,
            phone: cleanPhone,
          });
        } catch {
          /* hashing is best-effort; never block the conversion event */
        }
        analyticsSent = pushLeadEvent({
          event: "lead_submitted",
          event_id: eventId,
          form: "quick_quote",
          form_location: source,
          user_data: {
            email_address: cleanEmail,
            phone_number: cleanPhone,
            address: { first_name: firstName, last_name: lastName },
          },
          // SHA-256 hashed, Meta Advanced Matching format (em/ph/fn/ln).
          user_data_hashed: hashed,
          page_path: window.location.pathname,
        });
        try {
          localStorage.setItem("cp_lead_submitted", "1");
        } catch {
          /* ignore */
        }
      } catch {
        /* ignore analytics failures */
      }
      const params = new URLSearchParams({
        first_name: nameParts[0] ?? "",
        last_name: nameParts.slice(1).join(" "),
        email: cleanEmail,
        phone: `+1${cleanPhone}`,
      });
      // Only leave the page once GTM has fired the tags and the lead is saved.
      await Promise.all([analyticsSent, savePromise]);
      window.location.assign(`/thank-you?${params.toString()}`);

    })();
  }

  return (
    <form
      onSubmit={onSubmit}
      className={
        "rounded-lg bg-white/95 backdrop-blur-sm shadow-xl border border-white/40 p-5 " + className
      }
      aria-label="Quick free quote request"
    >
      {showHeader && (
        <>
          <p
            className="text-[var(--brand-deep-blue)] font-bold uppercase tracking-wide text-lg leading-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Get a Free Quote Fast
          </p>
          <p className="mt-1 text-[var(--brand-charcoal)]/75 text-sm">
            Tell us what you need cleaned and how to reach you.
          </p>
        </>
      )}

      <div className="mt-4 flex flex-col gap-3">
        <fieldset>
          <legend className="text-[11px] font-bold uppercase tracking-wide text-[var(--brand-charcoal)]/60">
            What do you need cleaned? (select all that apply)
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {SERVICE_OPTIONS.map((opt) => {
              const active = services.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleService(opt.value)}
                  className={
                    "rounded-full border px-3 py-2 text-xs font-semibold transition-colors " +
                    (active
                      ? "border-[var(--brand-deep-blue)] bg-[var(--brand-deep-blue)] text-white"
                      : "border-[var(--brand-light-gray)] bg-white text-[var(--brand-charcoal)]/80 hover:border-[var(--brand-bright-blue)]")
                  }
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {services.length >= 2 && (
            <p className="mt-2 text-[11px] font-semibold text-[var(--brand-red)]">
              Bundle discount applied — save 10% when we combine services.
            </p>
          )}
        </fieldset>
        <input
          type="text"
          autoComplete="name"
          required
          placeholder="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full min-h-[44px] rounded-md border border-[var(--brand-light-gray)] bg-white px-3 text-[var(--brand-charcoal)] text-base outline-none focus:border-[var(--brand-bright-blue)]"
        />
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          required
          placeholder="(518) 555-0123"
          value={phone}
          onChange={(e) => setPhone(formatPhone(e.target.value))}
          className="w-full min-h-[44px] rounded-md border border-[var(--brand-light-gray)] bg-white px-3 text-[var(--brand-charcoal)] text-base outline-none focus:border-[var(--brand-bright-blue)]"
        />
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="Email (optional)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full min-h-[44px] rounded-md border border-[var(--brand-light-gray)] bg-white px-3 text-[var(--brand-charcoal)] text-base outline-none focus:border-[var(--brand-bright-blue)]"
        />
      </div>

      {error && (
        <p role="alert" className="mt-2 text-[var(--brand-red)] text-xs font-medium">
          {error}
        </p>
      )}

      <SeasonNotice className="mt-4" />
      <button
        type="submit"
        disabled={busy}
        className="btn-pop mt-3 w-full inline-flex items-center justify-center min-h-[48px] rounded-md bg-[var(--brand-red)] text-white font-bold uppercase tracking-wide text-sm hover:bg-[var(--brand-red-hover)] transition-colors disabled:opacity-70"
      >
        {busy ? "Sending…" : "Get My Free Quote"}
      </button>
      <p className="mt-2 text-[11px] leading-snug text-[var(--brand-charcoal)]/60">
        By submitting, you agree to be contacted by phone or text about your quote.
      </p>
    </form>
  );
}
